import React, { useState } from "react";
import { useNavigate, useSearchParams, Link } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { useAuth } from "../context/AuthContext";
import { toast } from "sonner";
import { Users, Compass, ArrowLeft } from "lucide-react";
import SashaAvatar from "../components/SashaAvatar";

export default function LoginPage() {
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [busy, setBusy] = useState(false);
  const { login } = useAuth();
  const { t } = useTranslation();
  const navigate = useNavigate();
  const [params] = useSearchParams();
  const next = params.get("next") || "/portal";
  const restricted = params.get("restricted");
  const restrictionMsg = restricted
    ? {
        title: t("auth.restrictedTitle"),
        body:
          restricted === "admin"
            ? t("auth.restrictedAdmin")
            : t("auth.restrictedAdmin"),
      }
    : null;

  const submit = async (e) => {
    e.preventDefault();
    setBusy(true);
    try {
      await login(email, password);
      toast.success("Welcome back.");
      navigate(next);
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Sign in failed.");
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="max-w-6xl mx-auto px-6 lg:px-12 py-16 grid lg:grid-cols-2 gap-16 items-center" data-testid="login-page">
      <div className="hidden lg:block">
        <SashaAvatar size="lg" />
      </div>
      <div>
        {restrictionMsg && (
          <div
            className="mb-8 border-l-4 border-[var(--brand-red)] bg-[var(--ivory-2)] px-5 py-5 flex items-start gap-4"
            data-testid="login-restricted-banner"
          >
            <Compass size={22} strokeWidth={1.4} className="text-[var(--brand-red)] mt-1 shrink-0" />
            <div className="flex-1">
              <div className="font-display text-2xl text-[var(--emerald)] leading-tight">
                {restrictionMsg.title}
              </div>
              <p className="text-sm text-[var(--ink-2)] mt-2 leading-relaxed">
                {restrictionMsg.body}
              </p>
              <button
                type="button"
                onClick={() => (window.history.length > 1 ? navigate(-1) : navigate("/"))}
                className="mt-4 inline-flex items-center gap-2 text-xs uppercase tracking-[0.2em] text-[var(--brand-red)] border border-[var(--brand-red)]/50 hover:bg-[var(--brand-red)] hover:text-[var(--ivory)] px-4 py-2.5 transition-colors"
                data-testid="login-restricted-back"
              >
                <ArrowLeft size={13} strokeWidth={1.5} /> {t("auth.back")}
              </button>
            </div>
          </div>
        )}
        <div className="overline">{t("auth.signInCta")}</div>
        <h1 className="font-display text-5xl text-[var(--emerald)] mt-4">{t("auth.welcomeBack")}</h1>
        <p className="text-[var(--ink-2)] mt-3">{t("auth.sashaWaiting")}</p>
        <form onSubmit={submit} className="mt-10 space-y-6">
          <div>
            <div className="overline mb-1">Email</div>
            <input className="lux-input" type="email" required value={email}
              onChange={(e) => setEmail(e.target.value)} data-testid="login-email" />
          </div>
          <div>
            <div className="overline mb-1">Password</div>
            <input className="lux-input" type="password" required value={password}
              onChange={(e) => setPassword(e.target.value)} data-testid="login-password" />
          </div>
          <button type="submit" disabled={busy} className="btn-primary w-full" data-testid="login-submit">
            {busy ? "Signing in…" : "Sign in"}
          </button>
          <p className="text-sm text-[var(--ink-2)]">
            New to Sasha? <Link to="/register" className="text-[var(--gold)] hover:underline">Join Club Janté</Link>
          </p>
        </form>

        <div className="mt-10 pt-8 border-t border-[var(--emerald)]/15">
          <div className="overline">Travelling with friends?</div>
          <div className="mt-3 flex flex-wrap items-center justify-between gap-4">
            <p className="text-sm text-[var(--ink-2)] max-w-[280px] leading-snug">
              Plan one trip, invite your companions, and we'll bring everyone into the same view.
            </p>
            <button
              type="button"
              onClick={() => navigate("/groups/new")}
              className="text-xs uppercase tracking-[0.18em] text-[var(--brand-red)] hover:text-[var(--emerald)] inline-flex items-center gap-2 border border-[var(--brand-red)]/40 hover:border-[var(--emerald)] px-4 py-2.5"
              data-testid="login-start-group-btn"
            >
              <Users size={14} strokeWidth={1.5} /> Start a group trip
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}
